import {IntegrationCard, LinkedinAdsIcon} from "@tinct/web-components";
import {useGetCurrentWorkspaceId, useCurrentWorkspaceUrlPrefix} from "@tinct/core-workspace";
import {useDeleteCrmIntegration, useGetOrganizationById} from "@tinct/core-api";
import {useConfig} from "@tinct/core-config";
import {useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {useOAuthPopup} from "../pages/edit-integrations-page/hooks/use-oauth-popup";
import {getLinkedinTokenState} from "./linkedin-ads-token-state";

export const LinkedinAdsIntegration = () => {
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const navigate = useNavigate();
  const {isFeatureEnabled} = useConfig();

  const { mutate: deleteCrmIntegration } = useDeleteCrmIntegration();
  const { openOAuthPopup } = useOAuthPopup({
    organizationId: workspaceId,
    provider: 'linkedin_ads',
  });

  const { data: organization } = useGetOrganizationById({
    id: workspaceId,
    fields: {
      crmIntegrations: {
        fields: {
          id: null,
          providerType: null,
          status: null,
          errorDetails: null,
          // LinkedIn tokens die after 60 days with no refresh — the tile
          // warns before (and after) that happens.
          tokenExpiresAt: null,
          needsReauthorization: null,
        }
      }
    }
  }, {
    enabled: !!workspaceId,
  });

  const linkedinAdsIntegration = useMemo(() => {
    return organization?.crmIntegrations?.find(integration => integration.providerType === 'linkedin_ads');
  }, [organization]);

  const connected = !!linkedinAdsIntegration;
  const tokenState = getLinkedinTokenState(linkedinAdsIntegration);

  const handleDisconnect = () => {
    if (!workspaceId || !linkedinAdsIntegration?.id) return;
    deleteCrmIntegration({
      integrationId: linkedinAdsIntegration.id,
      organizationId: workspaceId,
    })
  }

  // Feature-flagged rollout: no tile at all while the workspace's
  // linkedin_ads_integration flag is off. (After every hook — the flag flips
  // when the config loads.)
  if (!isFeatureEnabled('linkedinAdsIntegration')) return null;

  const status = !connected
    ? 'not-connected'
    : tokenState.kind === 'expired'
      ? 'expired'
      : tokenState.kind === 'expiring'
        ? 'expiring'
        : 'connected';

  return <IntegrationCard
    title="LinkedIn Ads"
    description="Serve a dedicated page variant to visitors from each LinkedIn campaign"
    status={status}
    icon={<LinkedinAdsIcon width={40} height={40}/>}
    active={true}
    enabled={connected}
    // The switch stays connect/disconnect (matching every other tile); the
    // account picker and the reconnect CTA live on the detail page.
    onEnabledChange={(enabled) => {
      if (enabled) {
        openOAuthPopup();
      } else {
        handleDisconnect();
      }
    }}
    onSettingsClick={
      connected && linkedinAdsIntegration?.id
        ? () =>
            navigate(
              `${workspaceUrlPrefix}/settings/integrations/linkedin-ads/${linkedinAdsIntegration.id}`
            )
        : undefined
    }
  />
}
